/**
 * O que cada etapa do autocadastro exige antes de avançar. Espelha, do lado da
 * tela, a conferência do servidor (`conferencia-do-envio.ts` em
 * `apps/api/src/modulos/inscricao-publica/`): a tela avisa cedo, e o servidor
 * decide — o SGTP e o EOL são relidos no envio, e o que a tela aceitou pode
 * ainda voltar como pendência.
 *
 * Por etapa, e não do assistente inteiro: um esquema único reprovaria a
 * etapa 1 por causa de um campo da etapa 3 que ninguém viu ainda.
 */

import type {
  CicloDaInscricao,
  ContatosDoRascunho,
  DadosDoRascunho,
  VeiculoDoRascunho,
} from './apiDaInscricao';

export interface ProblemaDaEtapa {
  /** Placa do veículo, quando o problema é de um veículo. */
  placa: string | null;
  mensagem: string;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function veiculoConfigurado(veiculo: VeiculoDoRascunho, maxEscolas: number): boolean {
  return (
    veiculo.modalidade !== null &&
    veiculo.dreId !== null &&
    veiculo.escolas.length >= 1 &&
    veiculo.escolas.length <= maxEscolas
  );
}

export function problemasDaEtapa(
  etapa: number,
  estado: { dados: DadosDoRascunho; contatos: ContatosDoRascunho; ciclo: CicloDaInscricao },
): ProblemaDaEtapa[] {
  const { dados, contatos, ciclo } = estado;
  const problemas: ProblemaDaEtapa[] = [];

  if (etapa === 0) {
    const email = contatos.email?.trim() ?? '';
    if (!email) {
      problemas.push({
        placa: null,
        mensagem: 'Informe o e-mail: é por ele que a SMT fala com você sobre a inscrição.',
      });
    } else if (!EMAIL.test(email)) {
      problemas.push({ placa: null, mensagem: 'Confira o e-mail: ele parece incompleto.' });
    }
    const telefone = contatos.telefone?.replace(/\D/g, '') ?? '';
    if (telefone && (telefone.length < 10 || telefone.length > 11)) {
      problemas.push({ placa: null, mensagem: 'Confira o telefone: use DDD e número.' });
    }
    if (dados.veiculos.length === 0) {
      problemas.push({ placa: null, mensagem: 'Marque ao menos um veículo para inscrever.' });
    }
  }

  if (etapa === 1) {
    for (const veiculo of dados.veiculos) {
      if (!veiculo.modalidade) {
        problemas.push({ placa: veiculo.placa, mensagem: 'Escolha a modalidade.' });
      }
      if (!veiculo.dreId) {
        problemas.push({ placa: veiculo.placa, mensagem: 'Escolha a DRE.' });
      }
      if (veiculo.escolas.length < 1 || veiculo.escolas.length > ciclo.maxEscolas) {
        problemas.push({
          placa: veiculo.placa,
          mensagem: `Escolha de 1 a ${ciclo.maxEscolas} escola(s), em ordem de preferência.`,
        });
      }
    }
  }

  if (etapa === 2) {
    if (!dados.aceiteEdital || dados.editalVersao !== ciclo.editalVersao) {
      problemas.push({ placa: null, mensagem: 'Leia e aceite o edital para continuar.' });
    }
    if (!dados.aceiteLgpd) {
      problemas.push({ placa: null, mensagem: 'Aceite o aviso de privacidade para continuar.' });
    }
  }

  return problemas;
}
